import CloseIcon from '@mui/icons-material/Close';
import { AppBar, Box, CircularProgress, IconButton, Toolbar, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';

import throttle from 'lodash/throttle';
import QrScanner from 'qr-scanner';

import { CameraDevice, CameraPicker } from '@components/CameraPicker';
import { DialogDarkFullscreen } from '@components/DialogDarkFullscreen';

import { ActionComponentProps } from '../index';

type DialogQrCodeConfirmationProps = ActionComponentProps & {
  actionRequestInProgress: boolean;
  onQrCodeRecognized: (qrData: string) => void;
};

export const DialogQrCodeConfirmation = (props: DialogQrCodeConfirmationProps) => {
  const { actionStarted, actionRequestInProgress, onActionFinish, onQrCodeRecognized } = props;

  const [cameraDevices, setCameraDevices] = useState<CameraDevice[]>([]);
  const [qrScanner, setQrScanner] = useState<QrScanner | null>(null);

  const throttledOnQrCodeRecognized = useMemo(
    () =>
      throttle((qrData: string) => {
        onQrCodeRecognized(qrData);
      }, 1000),
    [onQrCodeRecognized],
  );

  useEffect(() => {
    const initCamerasList = async () => {
      const camerasList = await QrScanner.listCameras();
      setCameraDevices(camerasList.map((camera) => ({ deviceId: camera.id, label: camera.label })));
    };

    initCamerasList();
  }, []);

  useEffect(() => {
    if (!qrScanner) return;

    return () => {
      qrScanner.destroy();
    };
  }, [qrScanner]);

  const onVideoRefChange = useCallback(
    (videoElement: HTMLVideoElement | null) => {
      const scanner = videoElement
        ? new QrScanner(
            videoElement,
            (result) => {
              if (result.data) {
                throttledOnQrCodeRecognized(result.data);
              }
            },
            {},
          )
        : null;

      scanner?.start();
      setQrScanner(scanner);

      return () => {
        throttledOnQrCodeRecognized.cancel();
      };
    },
    [throttledOnQrCodeRecognized],
  );

  const changeCamera = (id: string) => {
    qrScanner?.setCamera(id);
  };

  return (
    <DialogDarkFullscreen open={actionStarted}>
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar>
          <IconButton color="inherit" disableRipple disabled={actionRequestInProgress} onClick={onActionFinish}>
            <CloseIcon />
          </IconButton>

          <Typography sx={{ ml: 2, flex: 1, textAlign: 'center' }} variant="h6" component="div">
            Scan Recipient’s QR Code
          </Typography>
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          overflow: 'auto',
        }}
      >
        {!actionStarted || actionRequestInProgress ? (
          <CircularProgress />
        ) : (
          <>
            <CameraPicker
              cameras={cameraDevices}
              onCameraChange={changeCamera}
              sx={{
                position: 'absolute',
                top: 20,
                right: 20,
              }}
            />

            <video
              ref={onVideoRefChange}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              }}
            />
          </>
        )}
      </Box>
    </DialogDarkFullscreen>
  );
};
